import { createPillButton } from '../ui/pillButton.js';
import { getNationalityEntry, normalizeCountryCode } from '@multirx/shared/types/nationalities.js';

export const MAX_GARAGE_CAR_NAME_LENGTH = 80;
export const MAX_GARAGE_DRIVER_NAME_PART_LENGTH = 48;

export type GarageRenameTextField = {
  kind?: 'text';
  id: string;
  label: string;
  value: string;
  maxLength: number;
  placeholder?: string;
};

export type GarageRenameSelectField = {
  kind: 'select';
  id: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
};

export type GarageRenameField = GarageRenameTextField | GarageRenameSelectField;

export function normalizeGarageCarName(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > MAX_GARAGE_CAR_NAME_LENGTH) return null;
  return trimmed;
}

export function normalizeGarageDriverNamePart(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > MAX_GARAGE_DRIVER_NAME_PART_LENGTH) return null;
  return trimmed;
}

export function normalizeGarageDriverNationality(raw: string): string | null {
  const code = normalizeCountryCode(raw);
  if (!code || !getNationalityEntry(code)) return null;
  return code;
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function renderRenameFieldHtml(field: GarageRenameField): string {
  if (field.kind === 'select') {
    const options = field.options
      .map(
        (option) =>
          `<option value="${escapeAttr(option.value)}"${option.value === field.value ? ' selected' : ''}>${escapeAttr(option.label)}</option>`
      )
      .join('');
    return `
        <label class="garage-rename-field-label" for="garage-rename-${field.id}">${escapeAttr(field.label)}</label>
        <select
          id="garage-rename-${field.id}"
          class="circuits-detail-rename-input garage-rename-select"
          data-field-id="${escapeAttr(field.id)}"
        >${options}</select>`;
  }

  return `
        <label class="visually-hidden" for="garage-rename-${field.id}">${escapeAttr(field.label)}</label>
        <input
          id="garage-rename-${field.id}"
          class="circuits-detail-rename-input"
          type="text"
          maxlength="${field.maxLength}"
          value="${escapeAttr(field.value)}"
          placeholder="${escapeAttr(field.placeholder ?? field.label)}"
          autocomplete="off"
          data-field-id="${escapeAttr(field.id)}"
        />`;
}

function readFieldValues(form: HTMLElement): Record<string, string> {
  const values: Record<string, string> = {};
  for (const el of form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-field-id]')) {
    values[el.dataset.fieldId!] = el.value;
  }
  return values;
}

export function mountGarageEntityRename(options: {
  section: HTMLElement;
  titleRow: HTMLElement;
  fields: GarageRenameField[];
  onSave: (values: Record<string, string>) => Promise<void>;
  onRestore: () => void;
}): void {
  const trigger = options.titleRow.querySelector('.circuits-detail-rename-trigger');
  const status = options.section.querySelector<HTMLElement>('.circuits-detail-rename-status');

  if (!trigger) return;

  trigger.addEventListener('click', () => {
    if (options.titleRow.querySelector('.circuits-detail-rename-form')) return;

    if (status) {
      status.hidden = true;
      status.textContent = '';
    }

    const fieldsHtml = options.fields.map((field) => renderRenameFieldHtml(field)).join('');

    options.titleRow.innerHTML = `
      <form class="circuits-detail-rename-form">
        <div class="garage-rename-fields">${fieldsHtml}</div>
        <div class="circuits-detail-rename-form-actions"></div>
      </form>`;

    const form = options.titleRow.querySelector('.circuits-detail-rename-form') as HTMLFormElement;
    const actions = options.titleRow.querySelector(
      '.circuits-detail-rename-form-actions'
    ) as HTMLElement;
    const focusTarget = options.titleRow.querySelector<HTMLInputElement | HTMLSelectElement>(
      '[data-field-id]'
    );

    actions.appendChild(
      createPillButton('Enregistrer', {
        variant: 'cta',
        small: true,
        onClick: () => form.requestSubmit(),
      })
    );
    actions.appendChild(
      createPillButton('Annuler', {
        variant: 'soft',
        small: true,
        onClick: () => options.onRestore(),
      })
    );

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void (async () => {
        const values = readFieldValues(form);

        try {
          if (status) {
            status.hidden = false;
            status.className = 'circuits-detail-rename-status';
            status.textContent = 'Enregistrement…';
          }
          await options.onSave(values);
        } catch (err) {
          if (status) {
            status.hidden = false;
            status.className = 'circuits-detail-rename-status circuits-detail-rename-status--error';
            status.textContent = err instanceof Error ? err.message : 'Échec de l’enregistrement.';
          }
        }
      })();
    });

    if (focusTarget instanceof HTMLInputElement) {
      focusTarget.focus();
      focusTarget.select();
    } else {
      focusTarget?.focus();
    }
  });
}
